import "@xyflow/react/dist/style.css";
import type React from "react";
import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {observer} from "mobx-react-lite";
import {
    Background,
    BackgroundVariant,
    Controls,
    getNodesBounds,
    getViewportForBounds,
    MiniMap,
    type NodeMouseHandler,
    type NodeTypes,
    Panel,
    ReactFlow,
    useReactFlow,
} from "@xyflow/react";
import {Box, Button, IconButton, Stack, Tooltip, useTheme} from "@mui/material";
import MergeIcon from "~/icons/svg/MergeIcon.svg?react";
import RedoIcon from "~/icons/svg/RedoIcon.svg?react";
import UndoIcon from "~/icons/svg/UndoIcon.svg?react";
import type {m_taxonomy} from "~/services/classes/TaxonomyClasses";
import {layoutTaxonomyTree, NODE_HEIGHT, NODE_WIDTH, type TaxonomyTreeNode} from "./taxonomyTreeLayout";
import {
    deepestSelected,
    expandedToLevel,
    indexTaxonomy,
    type LevelSelection,
    levelOfExpanded,
} from "./taxonomyTreeIndex";
import {SegmentedControl} from "~/components/segmented-control/SegmentedControl";
import {TaxonomyTreeNodeCard} from "./TaxonomyTreeNodeCard";
import {TaxonomyLevelFilter} from "./TaxonomyLevelFilter";
import {TaxonomyHoveredNodeCard} from "./TaxonomyHoveredNodeCard";
import {TaxonomyAddCategoryDialog} from "./TaxonomyAddCategoryDialog";
import {TaxonomyDeleteCategoryDialog} from "./TaxonomyDeleteCategoryDialog";
import {TaxonomyRenameCategoryDialog} from "./TaxonomyRenameCategoryDialog";
import {TaxonomyMoveCategoryDialog} from "./TaxonomyMoveCategoryDialog";
import {TaxonomyMergeBar} from "./TaxonomyMergeBar";
import {TaxonomyMergeCategoriesDialog} from "./TaxonomyMergeCategoriesDialog";
import {useTaxonomyTreeEditing} from "./useTaxonomyTreeEditing";
import {useTaxonomyTreeDragMove} from "./useTaxonomyTreeDragMove";
import {useTaxonomyHistory} from "~/pages/taxonomy-builder-v2/hooks/useTaxonomyHistory";
import {STICKY_HEADER_HEIGHT} from "~/pages/taxonomy-builder-v2/constants/taxonomyBuilderLayout";

const NODE_TYPES: NodeTypes = {taxonomy: TaxonomyTreeNodeCard};
const MIN_ZOOM = 0.1;

type TaxonomyTreeCanvasProps = {
    taxonomy: m_taxonomy.FullSerializer;
    /** Shown after the tree's controls above the canvas, e.g. the Table / Tree / Treemap switch. */
    toolbar?: React.ReactNode;
};

/**
 * Taxonomy tree on a pan / zoom canvas. Click a card to open or close its sub-categories.
 * The level filters (above the canvas) open the tree down to the chosen category, zoom to it and
 * draw its path in green. Users who can edit add and delete categories from a card's hover buttons,
 * rename one by clicking its name, move one by dragging its card onto the new parent, and merge
 * siblings from the Merge button (tick them, then name the result); each change is confirmed and then
 * saved straight away. Must be rendered inside a `ReactFlowProvider`.
 */
export const TaxonomyTreeCanvas: React.FC<TaxonomyTreeCanvasProps> = observer(({taxonomy, toolbar}) => {
    const theme = useTheme();
    const tree = taxonomy.diff_tree;
    // The root node is the taxonomy itself: its own label is not the taxonomy name.
    const name = taxonomy.name;
    // Opens with the L1 categories showing.
    const [expanded, setExpanded] = useState<ReadonlySet<number>>(() => new Set([tree.id]));
    const index = useMemo(() => indexTaxonomy(tree), [tree]);
    const [selection, setSelection] = useState<LevelSelection>([]);
    const selectedPath = useMemo(() => deepestSelected(index, selection)?.path ?? [], [index, selection]);
    const focusedId = selectedPath.at(-1);

    // Add / delete / rename / move: the card opens a dialog; the change is saved when it is confirmed.
    const editing = useTaxonomyTreeEditing(taxonomy);
    // Undo / redo: only for users who can edit, and not while an edit is saving or a merge is being picked.
    const history = useTaxonomyHistory(taxonomy);
    const [addUnder, setAddUnder] = useState<number | null>(null);
    const [deleteId, setDeleteId] = useState<number | null>(null);
    const [renaming, setRenaming] = useState<{id: number, to: string} | null>(null);
    const [moving, setMoving] = useState<{id: number, toId: number} | null>(null);
    // Merge mode: null when off; otherwise the ticked categories, all under one parent.
    const [mergeIds, setMergeIds] = useState<readonly number[] | null>(null);
    const [mergeConfirm, setMergeConfirm] = useState(false);
    const parentOf = useCallback((id: number) => index.byId.get(id)?.parentId ?? tree.id, [index, tree.id]);
    const mergeParentId = mergeIds?.length ? parentOf(mergeIds[0]) : undefined;
    const canHistory = editing.canEdit && !editing.isSaving && mergeIds === null;
    const toggleMerge = useCallback((id: number) => setMergeIds(prev => prev === null ? prev
        : prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]), []);

    // Adding under the deepest level gives the taxonomy a new level; its declared depth (`size`) is
    // left alone, as in the current builder.
    const deepestLevel = index.levels.length;

    const layout = useMemo(() => layoutTaxonomyTree(tree, name, expanded), [tree, name, expanded]);
    const laidOutNodes = useMemo(
        () => layout.nodes.map(n => {
            const id = Number(n.id);
            const isCategory = n.data.level > 0;
            // In merge mode the cards only tick; the other edits come back when it ends. None while an
            // undo / redo is saving: the edit would be saved on the state being replaced.
            const edit = editing.canEdit && mergeIds === null && !history.isPending;
            const data = {
                ...n.data,
                focused: id === focusedId || undefined,
                onAdd: edit ? () => setAddUnder(id) : undefined,
                onDelete: edit && isCategory ? () => setDeleteId(id) : undefined,
                // A name that only differs by surrounding whitespace is no rename: don't ask to confirm it.
                onRename: edit && isCategory
                    ? (label: string) => {
                        if (label.trim() !== n.data.label) setRenaming({id, to: label.trim()});
                    }
                    : undefined,
                onEditDescription: edit && isCategory ? () => setRenaming({id, to: n.data.label}) : undefined,
                mergeSelect: mergeIds !== null && isCategory
                    ? {
                        checked: mergeIds.includes(id),
                        // Once one is ticked, only its siblings can be.
                        disabled: mergeParentId !== undefined && parentOf(id) !== mergeParentId,
                        onToggle: () => toggleMerge(id),
                    }
                    : undefined,
            };
            // Every category but the root can be dragged onto another card to move it there.
            return {...n, draggable: edit && isCategory, data};
        }),
        [layout, focusedId, editing.canEdit, mergeIds, history.isPending, mergeParentId, parentOf, toggleMerge],
    );
    const onDrop = useCallback((id: number, toId: number) => setMoving({id, toId}), []);
    const {nodes, onNodesChange, onNodeDragStop} = useTaxonomyTreeDragMove({
        nodes: laidOutNodes,
        index,
        rootId: tree.id,
        onDrop,
    });
    // Edges from the root down to the category chosen in the filters are drawn in green.
    const edges = useMemo(() => {
        if (selectedPath.length === 0) return layout.edges;
        const pathIds = [tree.id, ...selectedPath].map(String);
        const onPath = new Set(pathIds.slice(1).map((id, i) => `${pathIds[i]}-${id}`));
        return layout.edges.map(e => onPath.has(e.id)
            ? {...e, zIndex: 1, style: {stroke: theme.palette.success.main, strokeWidth: 2.5}}
            : e);
    }, [layout.edges, selectedPath, tree.id, theme]);

    // Zoom to the chosen category once it is laid out (it may only appear after its parents open).
    // `setCenter` / `fitBounds` move the viewport right away; `fitView` would wait for a node update
    // that never comes when the card was already on screen.
    const {setCenter, fitBounds, getViewport, setViewport} = useReactFlow();
    const pendingFocus = useRef<string | null>(null);
    useEffect(() => {
        const node = nodes.find(n => n.id === pendingFocus.current);
        if (!node) return;
        pendingFocus.current = null;
        void setCenter(node.position.x + NODE_WIDTH / 2, node.position.y + NODE_HEIGHT / 2, {zoom: 1.1, duration: 600});
    }, [nodes, setCenter]);

    const onFilterChange = (next: LevelSelection) => {
        setSelection(next);
        const target = deepestSelected(index, next);
        if (!target) {
            void fitBounds(getNodesBounds(nodes), {padding: 0.1, duration: 600});
            return;
        }
        pendingFocus.current = String(target.id);
        // Open every ancestor so the target is on the canvas.
        setExpanded(prev => new Set([...prev, tree.id, ...target.path.slice(0, -1)]));
    };

    // Opening a category fits it and its sub-categories on screen: zoom out / pan only, never zoom in,
    // and leave the view alone when the whole branch is already visible.
    const canvasRef = useRef<HTMLDivElement>(null);
    // The filters and canvas together: merge mode scrolls them up under the sticky header, so the whole
    // canvas (and the merge bar along its bottom) is on screen while categories are ticked.
    // Scrolls once merge mode is drawn: started in the click, the redraw cancels the smooth scroll.
    const viewRef = useRef<HTMLDivElement>(null);
    const isMerging = mergeIds !== null;
    useEffect(() => {
        if (isMerging) viewRef.current?.scrollIntoView({behavior: 'smooth', block: 'start'});
    }, [isMerging]);
    const pendingReveal = useRef<string | null>(null);
    useEffect(() => {
        const id = pendingReveal.current;
        const canvas = canvasRef.current;
        if (!id || !canvas) return;
        pendingReveal.current = null;
        const childIds = new Set(layout.edges.filter(e => e.source === id).map(e => e.target));
        const branch = nodes.filter(n => n.id === id || childIds.has(n.id));
        if (branch.length <= 1) return;
        const bounds = getNodesBounds(branch);
        const {width, height} = canvas.getBoundingClientRect();
        const {x, y, zoom} = getViewport();
        const visible = bounds.x * zoom + x >= 0
            && bounds.y * zoom + y >= 0
            && (bounds.x + bounds.width) * zoom + x <= width
            && (bounds.y + bounds.height) * zoom + y <= height;
        if (visible) return;
        void setViewport(getViewportForBounds(bounds, width, height, MIN_ZOOM, zoom, 0.1), {duration: 600});
    }, [nodes, layout.edges, getViewport, setViewport]);

    // "Show levels": Ln opens every branch above level n (and closes the rest), then fits the whole tree.
    const levelLabels = index.levels.map((_, i) => `L${i + 1}`);
    const shownLevel = levelOfExpanded(index, tree.id, expanded);    const pendingFitAll = useRef(false);
    const showLevel = (levelIdx: number) => {
        pendingFitAll.current = true;
        setExpanded(expandedToLevel(index, tree.id, levelIdx + 1));
    };
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!pendingFitAll.current || !canvas) return;
        pendingFitAll.current = false;
        const {width, height} = canvas.getBoundingClientRect();
        void setViewport(getViewportForBounds(getNodesBounds(nodes), width, height, MIN_ZOOM, 1, 0.1), {duration: 600});
    }, [nodes, setViewport]);

    // Handlers keep their identity: React Flow hands them to every card, so a new one re-renders them all.
    const onNodeClick = useCallback<NodeMouseHandler<TaxonomyTreeNode>>((_, node) => {
        if (node.data.childCount === 0) return;
        const id = Number(node.id);
        if (!node.data.expanded) pendingReveal.current = node.id;
        setExpanded(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    }, []);
    const [hoveredId, setHoveredId] = useState<string | null>(null);
    const onNodeMouseEnter = useCallback<NodeMouseHandler<TaxonomyTreeNode>>((_, node) => setHoveredId(node.id), []);
    const onNodeMouseLeave = useCallback(() => setHoveredId(null), []);
    const hoveredNode = hoveredId ? nodes.find(n => n.id === hoveredId) : undefined;
    const hoveredParents = (index.byId.get(Number(hoveredId))?.path ?? []).slice(0, -1)
        .map(id => index.byId.get(id)?.label ?? '');

    const labelOf = (id: number) => id === tree.id ? name : index.byId.get(id)?.label ?? '';
    const childrenOf = (id: number) => id === tree.id
        ? index.levels[0] ?? []
        : [...index.byId.values()].filter(c => c.parentId === id);
    const confirmAdd = (parentId: number, label: string, description: string) =>
        editing.add(parentId, label, description, () => {
            setAddUnder(null);
            // Show the new category: open its parent and fit the branch on screen.
            pendingReveal.current = String(parentId);
            setExpanded(prev => new Set(prev).add(parentId));
        });
    /** The levels a move adds: set when the moved branch would reach below the deepest level. */
    const newLevelsForMove = (id: number, toId: number) => {
        const moved = index.byId.get(id);
        if (!moved) return undefined;
        const branch = [...index.byId.values()].filter(c => c.path.includes(id));
        const branchHeight = Math.max(...branch.map(c => c.depth)) - moved.depth;
        const bottom = (index.byId.get(toId)?.depth ?? 0) + 1 + branchHeight;
        return bottom > deepestLevel ? {first: deepestLevel + 1, last: bottom} : undefined;
    };
    const confirmMove = (id: number, toId: number) =>
        editing.move(id, labelOf(id), toId, toId === tree.id ? 'L1' : labelOf(toId), () => {
            setMoving(null);
            // Show the category in its new place: open the new parent and fit the branch on screen.
            pendingReveal.current = String(toId);
            setExpanded(prev => new Set(prev).add(toId));
        });
    // The ticked categories in tree order: the merged category takes the first one's place.
    const mergeSiblings = mergeParentId === undefined ? [] : childrenOf(mergeParentId);
    // In the order they were ticked: the merged category takes the place of the first, like the current builder.
    const mergeChosen = (mergeIds ?? []).flatMap(id => mergeSiblings.filter(c => c.id === id));
    const stopMerge = () => {
        setMergeConfirm(false);
        setMergeIds(null);
    };
    const confirmMerge = (parentId: number, name: string, description: string) =>
        editing.merge(parentId, mergeChosen.map(c => c.id), name, description, () => {
            stopMerge();
            pendingReveal.current = String(parentId);
            setExpanded(prev => new Set(prev).add(parentId));
        });

    return (
        // The level filters and level buttons (left) and the tree's actions (right), then the canvas
        // under them; the draft action bar is the page's, below it.
        <Stack ref={viewRef} gap={2} sx={{flex: 1, minHeight: 0, scrollMarginTop: STICKY_HEADER_HEIGHT}}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2}>
                <Stack direction="row" alignItems="center" gap={1} sx={{minWidth: 0}}>
                    <TaxonomyLevelFilter index={index} selection={selection} onChange={onFilterChange}/>
                    {levelLabels.length > 1 && (
                        // No button is picked while the open branches don't match one level.
                        <SegmentedControl
                            labels={levelLabels}
                            value={shownLevel === undefined ? -1 : shownLevel - 1}
                            onChange={showLevel}
                        />
                    )}
                </Stack>
                <Stack direction="row" alignItems="center" gap={1} sx={{flexShrink: 0}}>
                    {/* Shown only while there is something to undo / redo and it can be done now. */}
                    {canHistory && history.canUndo && (
                        <Tooltip title="Undo">
                            <IconButton size="small" aria-label="Undo" onClick={history.undo}>
                                <UndoIcon width={20} height={20}/>
                            </IconButton>
                        </Tooltip>
                    )}
                    {canHistory && history.canRedo && (
                        <Tooltip title="Redo">
                            <IconButton size="small" aria-label="Redo" onClick={history.redo}>
                                <RedoIcon width={20} height={20}/>
                            </IconButton>
                        </Tooltip>
                    )}
                    {editing.canEdit && mergeIds === null && (
                        <Button
                            size="small"
                            variant="outlined"
                            startIcon={<MergeIcon width={18} height={18}/>}
                            onClick={() => setMergeIds([])}
                        >
                            Merge
                        </Button>
                    )}
                    {toolbar}
                </Stack>
            </Stack>
            <Box
                ref={canvasRef}
                sx={{
                    flex: 1,
                    minHeight: 0,
                    // The box's height comes from flex, which `height: 100%` can't read; the canvas fills it absolutely.
                    position: 'relative',
                    border: '1px solid',
                    borderColor: 'grey.100',
                    borderRadius: '12px',
                    overflow: 'hidden',
                }}
            >
                <ReactFlow<TaxonomyTreeNode>
                    style={{position: 'absolute', inset: 0}}
                    nodes={nodes}
                    edges={edges}
                    nodeTypes={NODE_TYPES}
                    onNodeClick={onNodeClick}
                    onNodeMouseEnter={onNodeMouseEnter}
                    onNodeMouseLeave={onNodeMouseLeave}
                    onNodesChange={onNodesChange}
                    onNodeDragStop={onNodeDragStop}
                    // A small wobble while clicking a card still opens / closes it instead of dragging.
                    nodeDragThreshold={5}
                    defaultEdgeOptions={{style: {stroke: theme.palette.mithraBlue[500], strokeWidth: 1.5}}}
                    nodesConnectable={false}
                    nodesFocusable={false}
                    edgesFocusable={false}
                    elementsSelectable={false}
                    minZoom={MIN_ZOOM}
                    maxZoom={2}
                    fitView
                    fitViewOptions={{maxZoom: 1}}
                    proOptions={{hideAttribution: true}}
                >
                    <Background variant={BackgroundVariant.Dots} gap={20} size={1}/>
                    <Panel position="top-left">
                        <TaxonomyHoveredNodeCard node={hoveredNode} parents={hoveredParents}/>
                    </Panel>
                    {mergeIds !== null && (
                        <Panel position="bottom-center">
                            <TaxonomyMergeBar
                                selected={mergeChosen.map(c => c.label)}
                                parentName={mergeParentId === undefined ? undefined : mergeParentId === tree.id ? 'L1' : labelOf(mergeParentId)}
                                onCancel={stopMerge}
                                onMerge={() => setMergeConfirm(true)}
                            />
                        </Panel>
                    )}
                    <Controls showInteractive={false} position="bottom-left"/>
                    <MiniMap<TaxonomyTreeNode>
                        pannable
                        zoomable
                        nodeColor={n => n.data.level === 0 ? theme.palette.primary.main : theme.palette.grey[200]}
                        nodeStrokeColor={theme.palette.mithraBlue[300]}
                    />
                </ReactFlow>
            </Box>
            {addUnder !== null && (
                <TaxonomyAddCategoryDialog
                    parentPath={(index.byId.get(addUnder)?.path ?? []).map(labelOf)}
                    siblingNames={childrenOf(addUnder).map(c => c.label)}
                    newLevel={(index.byId.get(addUnder)?.depth ?? 0) === deepestLevel ? deepestLevel + 1 : undefined}
                    isSaving={editing.isSaving}
                    onCancel={() => setAddUnder(null)}
                    onSave={(label, description) => confirmAdd(addUnder, label, description)}
                />
            )}
            {deleteId !== null && (
                <TaxonomyDeleteCategoryDialog
                    label={labelOf(deleteId)}
                    descendantCount={[...index.byId.values()].filter(c => c.id !== deleteId && c.path.includes(deleteId)).length}
                    isSaving={editing.isSaving}
                    onCancel={() => setDeleteId(null)}
                    onConfirm={() => editing.remove(deleteId, labelOf(deleteId), () => setDeleteId(null))}
                />
            )}
            {renaming !== null && (
                <TaxonomyRenameCategoryDialog
                    from={labelOf(renaming.id)}
                    to={renaming.to}
                    description={index.byId.get(renaming.id)?.description ?? ''}
                    isSaving={editing.isSaving}
                    onCancel={() => setRenaming(null)}
                    onConfirm={description => editing.rename(
                        renaming.id,
                        labelOf(renaming.id),
                        renaming.to,
                        description,
                        () => setRenaming(null),
                    )}
                />
            )}
            {moving !== null && (
                <TaxonomyMoveCategoryDialog
                    label={labelOf(moving.id)}
                    descendantCount={[...index.byId.values()].filter(c => c.id !== moving.id && c.path.includes(moving.id)).length}
                    fromPath={(index.byId.get(moving.id)?.path ?? []).slice(0, -1).map(labelOf)}
                    toPath={moving.toId === tree.id ? [] : (index.byId.get(moving.toId)?.path ?? []).map(labelOf)}
                    newLevels={newLevelsForMove(moving.id, moving.toId)}
                    isSaving={editing.isSaving}
                    onCancel={() => setMoving(null)}
                    onConfirm={() => confirmMove(moving.id, moving.toId)}
                />
            )}
            {mergeConfirm && mergeParentId !== undefined && (
                <TaxonomyMergeCategoriesDialog
                    labels={mergeChosen.map(c => c.label)}
                    parentPath={(index.byId.get(mergeParentId)?.path ?? []).map(labelOf)}
                    otherSiblingNames={mergeSiblings.filter(c => !mergeIds?.includes(c.id)).map(c => c.label)}
                    subCategoryCount={mergeChosen.reduce((count, c) => count + childrenOf(c.id).length, 0)}
                    isSaving={editing.isSaving}
                    onCancel={() => setMergeConfirm(false)}
                    onConfirm={(name, description) => confirmMerge(mergeParentId, name, description)}
                />
            )}
        </Stack>
    );
});
